import React from 'react';
import { isEqual } from 'lodash';

type UseDndReorderListParams<T> = {
  elements: T[];
  onReorderDrop?: (elements: T[]) => void;
};

function withIndex<T>(elements: T[]) {
  return elements.map((element, index) => ({ ...element, index }));
}

export function useDndReorderList<T>({ elements, onReorderDrop }: UseDndReorderListParams<T>) {
  const prevList = React.useRef(elements);
  const [localList, setLocalList] = React.useState(withIndex(elements));

  if (!isEqual(prevList.current, elements)) {
    setLocalList(withIndex(elements));
    prevList.current = elements;
  }

  return React.useMemo(() => {
    const reorderHover = (dragIndex: number, hoverIndex: number) => {
      setLocalList((prevAttributes) => {
        const newAttributes = [...prevAttributes];
        const dragItem = newAttributes[dragIndex];
        newAttributes[dragIndex] = newAttributes[hoverIndex];
        newAttributes[hoverIndex] = dragItem;

        return newAttributes;
      });
    };

    const reorderDrop = () => {
      if (isEqual(localList, elements)) {
        return;
      }

      onReorderDrop?.(withIndex(localList));
    };

    return {
      elements: localList,
      reorderHover: onReorderDrop ? reorderHover : undefined,
      reorderDrop: onReorderDrop ? reorderDrop : undefined,
    };
  }, [localList, onReorderDrop, elements]);
}
